/**
 * interfaces
 */
import { IUnitBaseModel } from './interfaces';
import { IUnitBaseDto } from '../../interfaces';

/**
 * enums
 */
import { ThemeColorsVariants } from '@core/enums/theme-colors-variants';

/**
 * shared
 */
import { BadgeAppearance, BadgeModel, IBadge } from '@shared/directives';

/**
 * Abstract base class for all unit view-models.
 *
 * Wraps the common {@link IUnitBaseDto} shape and exposes every shared field
 * as a camelCase getter. The {@link hasChildrenBadge} getter is defined
 * **once here** and lazily cached — subclasses inherit it automatically.
 *
 * Subclasses must call `super(dto)` and declare only their own shape-specific
 * getters (e.g. `type`, `typeId`, `parentId`).
 *
 * @template TDto - A DTO type that extends {@link IUnitBaseDto}.
 *
 * @example
 * ```ts
 * class UnitModel extends UnitBaseModel<IUnitDto> implements IUnitModel { ... }
 * class UnitChildModel extends UnitBaseModel<IUnitChildDto> implements IUnitChildModel { ... }
 * ```
 */
export abstract class UnitBaseModel<TDto extends IUnitBaseDto = IUnitBaseDto> implements IUnitBaseModel<TDto> {

    /** Lazily initialized badge for the `has_children` field. */
    private _hasChildrenBadge!: IBadge<boolean>;

    /**
     * @param dto - Raw DTO returned by the API. Accessible by subclasses via `this.dto`.
     */
    constructor(public readonly dto: TDto) {}

    // ─── IUnitBaseDto getters ─────────────────────────────────────────────────

    /** @inheritdoc */
    public get id(): string {
        return this.dto.id;
    }

    /** @inheritdoc */
    public get name(): string {
        return this.dto.name;
    }

    /** @inheritdoc */
    public get description(): string | null {
        return this.dto.description;
    }

    /** @inheritdoc */
    public get level(): number {
        return this.dto.level;
    }

    /** @inheritdoc */
    public get latitude(): string {
        return this.dto.latitude;
    }

    /** @inheritdoc */
    public get longitude(): string {
        return this.dto.longitude;
    }

    /** @inheritdoc */
    public get xCoordinate(): string {
        return this.dto.x_coordinate;
    }

    /** @inheritdoc */
    public get yCoordinate(): string {
        return this.dto.y_coordinate;
    }

    /** @inheritdoc */
    public get htmlHexColor(): string | null {
        return this.dto.html_hex_color;
    }

    /** @inheritdoc */
    public get active(): boolean {
        return this.dto.active;
    }

    /** @inheritdoc */
    public get hasChildren(): boolean {
        return this.dto.has_children;
    }

    /** @inheritdoc */
    public get createdOn(): string {
        return this.dto.created_on;
    }

    // ─── Computed badge ───────────────────────────────────────────────────────

    /**
     * Lazily-cached badge reflecting whether this unit has children.
     *
     * - **Success / Yes** — unit has children
     * - **Secondary / No** — leaf unit
     *
     * Built once on first access; subsequent reads return the cached instance.
     */
    public get hasChildrenBadge(): IBadge<boolean> {
        if (this._hasChildrenBadge == null) {
            const value   = this.hasChildren;
            const label   = value ? 'GENERAL.YES' : 'GENERAL.NO';
            const variant = value ? ThemeColorsVariants.Success : ThemeColorsVariants.Secondary;
            this._hasChildrenBadge = new BadgeModel<boolean>(value, label, variant, BadgeAppearance.Label);
        }
        return this._hasChildrenBadge;
    }

}
